import { useEffect, useRef } from 'react';
import { getLastActivity } from '@/lib/api/client';

/**
 * Hook for tracking user inactivity and triggering logout.
 *
 * Tracks local activity events (mousedown, keydown, scroll, touchstart).
 * When the local timer expires, checks server-side activity (via Gateway + Redis)
 * before triggering logout — this covers cross-tab activity on merchant subdomains.
 *
 * @param timeoutMinutes - Inactivity timeout in minutes (0 = disabled)
 * @param onTimeout - Callback function to execute when timeout expires
 * @param enabled - Enable/disable tracking (default: true)
 */
export const useInactivityTimeout = (
  timeoutMinutes: number = 0,
  onTimeout: () => void,
  enabled: boolean = true
): void => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onTimeoutRef = useRef(onTimeout);

  // Update callback ref when it changes
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    // Don't setup if disabled or timeout is 0 (never)
    if (!enabled || timeoutMinutes === 0) {
      return;
    }

    const timeoutMs = timeoutMinutes * 60 * 1000;

    const checkServerActivity = async (): Promise<boolean> => {
      try {
        const { lastActivity, serverTime } = await getLastActivity();
        if (!lastActivity) return false;

        const inactiveMs = serverTime - lastActivity;
        return inactiveMs < timeoutMs;
      } catch {
        return false; // API error — treat as inactive (safe default)
      }
    };

    const resetTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(async () => {
        // Server-side check before logout
        const activeOnServer = await checkServerActivity();
        if (activeOnServer) {
          console.log('[Auth] Active on server — resetting inactivity timer');
          resetTimeout();
        } else {
          console.log('[Auth] Inactivity confirmed by server — logging out');
          onTimeoutRef.current();
        }
      }, timeoutMs);
    };

    // Activity event handler
    const handleActivity = () => {
      resetTimeout();
    };

    // Events to track
    const events: (keyof DocumentEventMap)[] = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
    ];

    // Start initial timeout
    resetTimeout();

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    console.log(`Inactivity timeout enabled: ${timeoutMinutes} minutes`);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });

      console.log('Inactivity timeout disabled');
    };
  }, [timeoutMinutes, enabled]);
};
